import { useCanvasById } from "@/api/canvas";
import { useCancelFlowSession, useFlowSessionById } from "@/api/flow_sessions";
import { useOrgIntegrationConnections } from "@/api/integrations";
import { useSkills } from "@/api/skills";
import { useUsageLimitReason } from "@/api/user";
import { usePrivateWorkspace } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { hasPlatformEntitySelector } from "@/constants/integration-platforms";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { validateChatMessageFiles } from "@/constants/file-limits";
import {
  MessageInputControl,
  MessageInputControlSet,
  MessageInputControlUtils,
} from "@/constants/message-input";
import {
  allowedExtensions,
  allowedMimeTypes,
} from "@/hooks/useFileRecordUpload";
import { useLexicalMessageAbstraction } from "@/hooks/useLexicalMessageAbstraction";
import { filterSkillsForPicker } from "@/utils/filterSkillsForPicker";
import { mergeMessageAndCanvasReferencedEntities } from "@/utils/mergeReferencedEntities";
import {
  AttachedFile,
  IntegrationConnection,
  PostMessageParams,
  ReferencedEntity,
  type FileRecord,
} from "@cohesive/api-client";

import {
  ComposerShell,
  ConnectionNode,
  TRIGGER_CONNECTION_MENU_COMMAND,
  TRIGGER_SKILL_MENU_COMMAND,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useRect } from "@radix-ui/react-use-rect";
import {
  $getNodeByKey,
  BLUR_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  FOCUS_COMMAND,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
} from "lexical";
import { AlertTriangle, Braces, Mic } from "lucide-react";
import {
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { EditorContent } from "./Lexical/Editor";
import { MessageInputFilePreview } from "./components/MessageInputFilePreview";
import { resolveMentionQuery } from "./mentionQuery";

import { AgentReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { cn } from "@/utils";
import { useMicrophonePermission } from "@/utils/microphonePermissions";

import Fuse from "fuse.js";
import React from "react";
import toast from "react-hot-toast";

import { INSERT_PLACEHOLDER_COMMAND } from "@cohesive/ui-components";
import { AddMenuButton } from "./components/AddMenuButton";
import {
  AudioRecordingButton,
  AudioRecordingButtonRef,
} from "./components/AudioRecordingButton";
import { ReasoningEffortButton } from "./components/ReasoningEffortButton";
import { SendButton } from "./components/SendButton";
import { SessionBypassNotice } from "./components/SessionBypassNotice";
import { SkillsMenu } from "./components/SkillsMenu";
import { useMessageDraft } from "./hooks/useMessageDraft";
import { useStoreEditorOnScreen } from "./hooks/useStoreEditorOnScreen";

interface MessageInputProps {
  sendButtonDisabled?: boolean;
  onSend?: (params: PostMessageParams) => Promise<void>;
  onReferencedEntitiesChange?: (entities: ReferencedEntity[]) => void;
  attachedFiles: AttachedFile[];
  /** Merged into each outgoing message after editor @-mention entities (e.g. canvas selection). */
  referencedEntities?: ReferencedEntity[];
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  autoFocus?: boolean;
  /**
   * After a send resolves, the composer re-grabs focus so the user can keep
   * typing. Set false when the send hands focus off elsewhere — e.g. the home
   * composer opening the floating chat window, whose own input should take
   * focus instead of this one stealing it back a frame later.
   */
  refocusOnSend?: boolean;
  onMessageChange?: (message: string) => void;
  flowSessionId?: string;
  /** Used for draft persistence when no flow session exists (e.g. canvas quick chat). */
  canvasId?: string;
  onIntegrationSelect?: (integrationId: string) => void;
  onAttachFile?: (files: File[]) => void;
  /** Attach existing Library files as live references (no copy). Enables the
   *  add-menu's "Add from Library" together with `libraryWorkspaceId`. */
  onAttachFileRecords?: (records: FileRecord[]) => void;
  /** The workspace whose Library the picker searches. */
  libraryWorkspaceId?: string;
  onRemoveFile?: (file: AttachedFile) => void;
  onMenuChange?: (open: boolean) => void;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  location?: "sidebar" | "main";
  className?: string;
  classNames?: MessageInputClassNames;

  /**
   * When true, the Lexical editor is made non-editable — the user
   * cannot type or modify the composer text. Programmatic hydration
   * (`defaultValue`) and the Send button still work. Used by the
   * onboarding chat so the user can only send the scripted prompt.
   */
  editingLocked?: boolean;

  /**
   * Controls which sections are visible in the MessageInput.
   * Use MessageInputControl constants for type safety.
   * @example
   * // Show only chat and microphone
   * controlSet={[MessageInputControl.AGENT_SELECTOR, MessageInputControl.MICROPHONE]}
   *
   * // Hide everything (minimal input)
   * controlSet={[]}
   *
   * // Use preset configurations
   * controlSet={MessageInputControlSet.DEFAULT}
   */
  controlSet?: MessageInputControlSet;

  addPlaceholderMode?: "add" | "edit";

  /**
   * Renders between the left toolbar (add context, skills, …) and the right cluster (suggestions, mic, send), e.g. canvas selection chips.
   */
  middleToolbarSlot?: React.ReactNode;

  /**
   * Current agent reasoning effort level. Only rendered when REASONING_EFFORT is in the control set.
   * Persisted externally (e.g. localStorage via useCanvasReasoningEffort).
   */
  agentReasoningEffort?: AgentReasoningEffort | undefined;

  /**
   * Called when the user selects a different reasoning effort from the in-input picker.
   */
  onAgentReasoningEffortChange?: (effort: AgentReasoningEffort) => void;
}

export interface MessageInputClassNames {
  container?: string;
  contentWrapper?: string;
  editor?: string;
  placeholder?: string;
  leftControls?: string;
  rightControls?: string;
  toolbarRow?: string;
  menuIcon?: string;
  microphoneIcon?: string;
  placeholderIcon?: string;
  sendIcon?: string;
  /** Extra classes for the send `Button` (regular message send). */
  sendButton?: string;
  /** Extra classes for the stop `Button` when the flow session is processing. */
  stopButton?: string;
  /** Add / Skills / Suggestions / agent selector icon buttons (outer hit target). */
  toolbarIconButton?: string;
  /** Mic permission prompt button when recording API not yet enabled. */
  microphoneButton?: string;
  /** Passed to `AudioRecordingButton` `className` (mic/stop/error; not the send-while-recording control). */
  audioRecordingButton?: string;
}

export interface MessageInputRef {
  setValue: (value: string) => void;
  getTransformedMessage: () => string;
}

const defaultClassNames: Required<MessageInputClassNames> = {
  container: "w-full sm:max-w-[776px] mx-auto",
  contentWrapper: "",
  editor: "min-h-[30px]",
  placeholder: "",
  leftControls: "flex flex-wrap items-center gap-0.5 order-first",
  rightControls: "flex items-center gap-1",
  toolbarRow: "",
  menuIcon: "",
  microphoneIcon: "",
  placeholderIcon: "",
  sendIcon: "",
  sendButton: "",
  stopButton: "",
  toolbarIconButton: "",
  microphoneButton: "",
  audioRecordingButton: "",
};

/** Stable default for omitted `referencedEntities` so memo deps do not change every render. */
const EMPTY_REFERENCED_ENTITIES: ReferencedEntity[] = [];

const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  (
    {
      sendButtonDisabled,
      onSend,
      onReferencedEntitiesChange,
      onAttachFile,
      onAttachFileRecords,
      libraryWorkspaceId,
      attachedFiles,
      referencedEntities:
        externalReferencedEntities = EMPTY_REFERENCED_ENTITIES,
      onRemoveFile,
      placeholder: propPlaceholder = "How can Cohesive help you today?",
      defaultValue,
      value: propValue,
      onMessageChange,
      onMenuChange,
      autoFocus = false,
      refocusOnSend = true,
      onBlur,
      onFocus,
      location = "main",
      flowSessionId,
      canvasId,
      addPlaceholderMode = "add",
      className,
      classNames,
      editingLocked = false,
      middleToolbarSlot,
      agentReasoningEffort = undefined,
      onAgentReasoningEffortChange,
      // Control set
      controlSet = MessageInputControlSet.DEFAULT,
    },
    ref,
  ) => {
    const resolvedClassNames = {
      ...defaultClassNames,
      ...classNames,
    };
    // Check usage limits
    const {
      reason: usageLimitReason,
      isUsageAllowed,
      hasPaymentMethod,
    } = useUsageLimitReason();
    const isUsageDisabled = !isUsageAllowed;

    // Use the provided control set directly
    const resolvedVisibleSections = useMemo((): MessageInputControl[] => {
      return [...controlSet] as MessageInputControl[];
    }, [controlSet]);

    useStoreEditorOnScreen(location);

    const [editor] = useLexicalComposerContext();

    // `editingLocked` (onboarding chat) makes the editor non-editable so
    // the user can't go off-script. Lexical still accepts programmatic
    // updates — `defaultValue` hydration and Send are unaffected.
    useEffect(() => {
      editor.setEditable(!editingLocked);
      return () => editor.setEditable(true);
    }, [editor, editingLocked]);

    const { data: flowSession } = useFlowSessionById(flowSessionId || "");
    const cancelFlowSession = useCancelFlowSession();

    // The add-menu's Library picker searches the composer's workspace. An
    // explicit prop wins; otherwise derive from the session, then the canvas,
    // then fall back to the private workspace (home composers).
    const { data: composerCanvas } = useCanvasById(canvasId ?? "");
    const privateWorkspace = usePrivateWorkspace();
    const resolvedLibraryWorkspaceId =
      libraryWorkspaceId ??
      flowSession?.workspace_id ??
      composerCanvas?.workspace_id ??
      privateWorkspace?.id;

    const [editorKey, setEditorKey] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const containerRect = useRect(containerRef.current);

    const { message, setMessage, getTransformedMessage } =
      useLexicalMessageAbstraction();

    const { data: skills = [] } = useSkills();

    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);

    // LaunchDarkly flags
    const flags = useFlags<Flags>();

    // Microphone permissions
    const {
      isGranted: microphoneGranted,
      requestPermission: requestMicrophonePermission,
    } = useMicrophonePermission();

    // Audio recording state and command key handling
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const commandPressCountRef = useRef(0);
    const commandTimerRef = useRef<NodeJS.Timeout | null>(null);
    const audioRecordingRef = useRef<AudioRecordingButtonRef | null>(null);

    // Check if running on Mac
    const isMac = useMemo(() => {
      return (
        typeof navigator !== "undefined" &&
        navigator.platform.toUpperCase().indexOf("MAC") >= 0
      );
    }, []);

    // Handle command key double-tap for audio recording
    const handleCommandKeyPress = useCallback(() => {
      if (!isMac || !isInputFocused) return;

      commandPressCountRef.current += 1;

      if (commandTimerRef.current) {
        clearTimeout(commandTimerRef.current);
      }

      if (commandPressCountRef.current === 1) {
        // Start timer for double-tap detection (300ms window)
        commandTimerRef.current = setTimeout(() => {
          commandPressCountRef.current = 0;
        }, 300);
      } else if (commandPressCountRef.current === 2) {
        // Double-tap detected - toggle recording
        commandPressCountRef.current = 0;
        if (commandTimerRef.current) {
          clearTimeout(commandTimerRef.current);
          commandTimerRef.current = null;
        }

        // Toggle audio recording
        if (isRecording) {
          audioRecordingRef.current?.stopRecording();
        } else {
          audioRecordingRef.current?.startRecording();
        }
      }
    }, [isMac, isInputFocused, isRecording]);

    const placeholder = propPlaceholder;

    const {
      data: integrationConnections = [],
      isPending: connectionsLoading,
      isError: connectionsError,
    } = useOrgIntegrationConnections();

    // File upload visibility
    const showFileUpload = MessageInputControlUtils.isVisible(
      resolvedVisibleSections,
      MessageInputControl.FILE_UPLOAD,
    );

    // Entity selector visibility
    const showEntitySelector = MessageInputControlUtils.isVisible(
      resolvedVisibleSections,
      MessageInputControl.ENTITY_SELECTOR,
    );

    // Show add menu if either file upload or entity selector is enabled.
    // Suppressed while `editingLocked` (onboarding chat) so the user
    // can't attach files or @-mention connections off-script.
    const showAddMenu =
      (showFileUpload || showEntitySelector) && !editingLocked;

    const showSkills =
      MessageInputControlUtils.isVisible(
        resolvedVisibleSections,
        MessageInputControl.SKILLS,
      ) && !editingLocked;
    const showPlaceholders = MessageInputControlUtils.isVisible(
      resolvedVisibleSections,
      MessageInputControl.PLACEHOLDERS,
    );
    const showMicrophone =
      MessageInputControlUtils.isVisible(
        resolvedVisibleSections,
        MessageInputControl.MICROPHONE,
      ) && flags.audioTranscription;
    const showSendButton = MessageInputControlUtils.isVisible(
      resolvedVisibleSections,
      MessageInputControl.SEND_BUTTON,
    );

    // Only enable microphone functionality if permission is granted
    const microphoneEnabled = showMicrophone && microphoneGranted;

    // Connection filtering logic - only include connections with entity selectors
    const connectionsWithSelectors = useMemo(() => {
      return integrationConnections.filter((connection) =>
        hasPlatformEntitySelector(connection.integration_platform),
      );
    }, [integrationConnections]);

    // Whether the add menu offers connectors at all — an empty roster still
    // renders the section so its zero state can point at connector settings.
    const connectionsEnabled = showEntitySelector;
    const hasConnections = connectionsWithSelectors.length > 0;
    const fileUploadEnabled = showFileUpload;
    const availableSkillsForPicker = useMemo(
      () => filterSkillsForPicker(skills),
      [skills],
    );

    const skillsEnabled = availableSkillsForPicker.length > 0 && showSkills;

    // Reasoning-effort ("mode") picker — hidden while `editingLocked`
    // so the onboarding chat stays on its scripted configuration.
    const showReasoningEffort =
      MessageInputControlUtils.isVisible(
        resolvedVisibleSections,
        MessageInputControl.REASONING_EFFORT,
      ) && !editingLocked;

    const [activeConnection, setActiveConnection] =
      useState<IntegrationConnection | null>(null);
    const [skillsFilter, setSkillsFilter] = useState("");
    const [connectionsFilter, setConnectionsFilter] = useState("");
    const [showConnectionsInAddMenu, setShowConnectionsInAddMenu] =
      useState(false);
    const [mentionMode, setMentionMode] = useState<"@" | "/" | null>(null);
    const [mentionStartPos, setMentionStartPos] = useState<number>(0);

    const integrationConnectionSearch = useMemo(() => {
      return new Fuse(connectionsWithSelectors, {
        keys: ["integration_name"],
        threshold: 0.2,
      });
    }, [connectionsWithSelectors]);

    const filteredConnections = useMemo(() => {
      if (connectionsFilter === "") return connectionsWithSelectors;
      const results = integrationConnectionSearch.search(connectionsFilter);
      return results.map((result) => result.item);
    }, [
      integrationConnectionSearch,
      connectionsFilter,
      connectionsWithSelectors,
    ]);

    const skillsSearch = useMemo(() => {
      return new Fuse(availableSkillsForPicker, {
        keys: ["name"],
        threshold: 0.5,
      });
    }, [availableSkillsForPicker]);

    const filteredSkills = useMemo(() => {
      if (skillsFilter === "") return availableSkillsForPicker;
      const results = skillsSearch.search(skillsFilter);
      return results.map((result) => result.item);
    }, [skillsSearch, skillsFilter, availableSkillsForPicker]);

    const [editorReferencedEntities, setEditorReferencedEntities] = useState<
      ReferencedEntity[]
    >([]);

    const draftPersistenceEnabled =
      !editingLocked && propValue === undefined && defaultValue === undefined;

    const { clearDraft } = useMessageDraft({
      flowSessionId,
      canvasId,
      enabled: draftPersistenceEnabled,
      onRestoreReferencedEntities: setEditorReferencedEntities,
    });

    const sendPayloadReferencedEntities = useMemo(
      () =>
        mergeMessageAndCanvasReferencedEntities(
          editorReferencedEntities,
          externalReferencedEntities,
        ),
      [editorReferencedEntities, externalReferencedEntities],
    );

    useEffect(() => {
      if (propValue) {
        setMessage(propValue);
      }
    }, [propValue]);

    // Flush `defaultValue` into Lexical whenever it changes to a new
    // non-empty string. The ref stores the last-flushed value so we
    // don't re-inject the same string twice (which would overwrite the
    // user's edits) but we DO inject when the prop changes to a
    // different string — e.g. onboarding Step 5 prompt → Step 7 prompt
    // on the same mounted MessageInput instance.
    //
    // We watch `defaultValue` (not mount-only) because the prop often
    // arrives on a subsequent render: the onboarding context sets
    // `pinnedComposerPrompt` in a `useEffect` that runs after the
    // panel's first commit, so `MessageInput` mounts with
    // `defaultValue = undefined` and receives the real string a frame later.
    const defaultValueFlushedRef = useRef<string | null>(null);
    useEffect(() => {
      // Transition from a hydrated default → no default: clear the
      // editor. This handles the onboarding case where a step pins a
      // composer prompt, the user backs out of the precondition (e.g.
      // deselects a required node), and the step nulls the pinned
      // prompt. Without this clear, the stale text would remain and
      // the user could still send it — sending a message that no
      // onboarding step is listening for, leaving the flow stuck.
      if (!defaultValue) {
        if (defaultValueFlushedRef.current !== null) {
          defaultValueFlushedRef.current = null;
          setMessage("");
        }
        return;
      }
      if (defaultValueFlushedRef.current === defaultValue) return;
      defaultValueFlushedRef.current = defaultValue;
      setMessage(defaultValue);
    }, [defaultValue, setMessage]);

    useEffect(() => {
      if (!addMenuOpen) {
        setConnectionsFilter("");
        setShowConnectionsInAddMenu(false);
        setActiveConnection(null);
      }
    }, [addMenuOpen]);

    useImperativeHandle(ref, () => ({
      setValue: (newValue: string) => {
        setMessage(newValue);
      },
      getTransformedMessage: () => getTransformedMessage(),
    }));

    const isSendButtonDisabled = useMemo(() => {
      // Disable if usage is restricted
      if (isUsageDisabled) {
        return true;
      }
      if (sendButtonDisabled) {
        return true;
      }
      if (message.trim() === "" && attachedFiles.length === 0) {
        return true;
      }

      if (attachedFiles.length > 0) {
        const pendingFiles = attachedFiles.filter(
          (file) => file.status === "pending" || file.status === "initializing",
        );
        if (pendingFiles.length > 0) {
          return true;
        }

        // Gate on the shared per-file size cap + per-message count cap.
        const filesWithFile = attachedFiles.map((af) => ({
          file: af.file,
          name: af.name,
        }));
        const validation = validateChatMessageFiles(filesWithFile);
        if (!validation.isValid) {
          return true;
        }
      }
      return false;
    }, [isUsageDisabled, sendButtonDisabled, message, attachedFiles]);

    const menuOpen = Boolean(
      addMenuOpen || skillsMenuOpen || showConnectionsInAddMenu,
    );

    useEffect(() => {
      onMenuChange?.(menuOpen);
    }, [menuOpen]);

    useEffect(() => {
      return editor.registerMutationListener(
        ConnectionNode,
        (mutatedNodes, { prevEditorState }) => {
          for (let [nodeKey, mutation] of mutatedNodes) {
            if (mutation === "destroyed") {
              editor.read(() => {
                const connectionNode = $getNodeByKey(
                  nodeKey,
                  prevEditorState,
                ) as ConnectionNode;
                const connectionPayload = connectionNode.__connection;
                const { platform, type, identifier, title } = connectionPayload;

                setEditorReferencedEntities((prev) => {
                  let foundMatch = false;
                  return prev.filter((e) => {
                    if (
                      !foundMatch &&
                      e.entity_id === identifier &&
                      e.entity_type === type &&
                      e.platform === platform &&
                      e.name === title
                    ) {
                      foundMatch = true;
                      return false;
                    }
                    return true;
                  });
                });
              });
            }

            if (mutation === "created") {
              editor.read(() => {
                const connectionNode = $getNodeByKey(nodeKey) as ConnectionNode;
                const connectionPayload = connectionNode.__connection;
                const { platform, type, identifier, title } = connectionPayload;

                const entity: ReferencedEntity = {
                  entity_id: identifier || "",
                  entity_type: type || "",
                  name: title || "",
                  platform: platform?.toLowerCase() || "",
                };

                setEditorReferencedEntities((prev) => [...prev, entity]);
              });
            }
          }
        },
      );
    }, [editor]);

    useEffect(() => {
      return editor.registerCommand(
        PASTE_COMMAND,
        (event: ClipboardEvent) => {
          if (event.clipboardData?.files) {
            const files = Array.from(event.clipboardData?.files || []);
            if (files.length > 0) {
              const validFiles: File[] = [];

              files.forEach((file) => {
                const fileExtension = file.name.split(".").pop()?.toLowerCase();
                if (
                  fileExtension &&
                  allowedExtensions.includes(fileExtension) &&
                  allowedMimeTypes[
                    fileExtension as keyof typeof allowedMimeTypes
                  ] === file.type
                ) {
                  validFiles.push(file);
                } else {
                  toast.error(`Unsupported file type: ${file.name}`);
                }
              });

              if (validFiles.length > 0) {
                onAttachFile?.(validFiles);
              }
            }
          }

          return false;
        },
        1,
      );
    }, [editor]);

    useEffect(() => {
      return editor.registerCommand(
        BLUR_COMMAND,
        (event) => {
          setIsInputFocused(false);
          onBlur?.(event);

          return false;
        },
        1,
      );
    }, [editor]);

    useEffect(() => {
      return editor.registerCommand(
        FOCUS_COMMAND,
        (event) => {
          setIsInputFocused(true);
          onFocus?.(event);

          return false;
        },
        1,
      );
    }, [editor]);

    // Add keyboard event listeners for command key double-tap detection
    useEffect(() => {
      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key === "Meta" || event.key === "Cmd") {
          // Meta key is Command on Mac
          event.preventDefault();
          handleCommandKeyPress();
        }
      };

      if (isMac && isInputFocused) {
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
        };
      }
    }, [isMac, isInputFocused, handleCommandKeyPress]);

    // Cleanup command timer on unmount
    useEffect(() => {
      return () => {
        if (commandTimerRef.current) {
          clearTimeout(commandTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      // any time the message is reset, we want to change the editor key
      // which will cause a force re-render of the editor. This fixes a bug
      // where iOS will not auto-capitalize the first letter of the message
      if (message === "") {
        setEditorKey((prev) => prev + 1);
      }

      onMessageChange?.(message);
    }, [message]);

    /**
     * Leaves mention mode and dismisses the menu the trigger opened, handing
     * the keyboard back to the composer.
     */
    const abandonMention = useCallback((mode: "@" | "/") => {
      setMentionMode(null);
      if (mode === "@") {
        setAddMenuOpen(false);
        setShowConnectionsInAddMenu(false);
        setConnectionsFilter("");
        return;
      }
      setSkillsMenuOpen(false);
      setSkillsFilter("");
    }, []);

    // Real-time filtering for @ and / mentions, and the place the menu closes
    // when the mention turns out to be prose. The popover takes keyboard focus
    // when it opens, but Lexical pulls focus back to the composer whenever it
    // re-applies the editor selection — so the space that ends a mention can
    // land either in the menu's search field or in the message itself, and
    // reading the message text catches both.
    useEffect(() => {
      return editor.registerTextContentListener((textContent) => {
        if (!mentionMode) return;

        const resolved = resolveMentionQuery(textContent, mentionStartPos);
        if (resolved.outcome === "prose") {
          abandonMention(mentionMode);
          return;
        }
        if (resolved.outcome === "triggerGone") {
          setMentionMode(null);
          return;
        }

        if (mentionMode === "@") {
          setConnectionsFilter(resolved.query);
        } else {
          setSkillsFilter(resolved.query);
        }
      });
    }, [editor, mentionMode, mentionStartPos, abandonMention]);

    // Reset mention mode when menus close
    useEffect(() => {
      if (!addMenuOpen) {
        if (mentionMode === "@") {
          setMentionMode(null);
          setConnectionsFilter("");
        }
      }
    }, [addMenuOpen, mentionMode]);

    useEffect(() => {
      if (!skillsMenuOpen) {
        if (mentionMode === "/") {
          setMentionMode(null);
          setSkillsFilter("");
        }
      }
    }, [skillsMenuOpen, mentionMode]);

    const handleSendMessage = async () => {
      if (isSendButtonDisabled) {
        return;
      }

      // Get transformed message (with config nodes converted to placeholders/connections)
      const transformedMessage = getTransformedMessage();

      await onSend?.({
        id: uuidv4(),
        content: transformedMessage?.trim(),
        referenced_entities: sendPayloadReferencedEntities,
        reasoning_effort: agentReasoningEffort,
      });

      setMessage("");
      setEditorReferencedEntities([]);
      clearDraft();
      // agentReasoningEffort is intentionally NOT reset — it persists across sends

      if (refocusOnSend) {
        setTimeout(() => {
          focusTextarea();
        }, 0);
      }
    };

    const handleStopFlowSession = () => {
      if (flowSessionId) {
        cancelFlowSession.mutate(flowSessionId);
      }
    };

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();

          if (typeof onSend === "function") {
            handleSendMessage();
          }

          return true;
        }

        // @ and / handling moved to ConnectionPlugin and SkillPlugin

        if (e.key === "Escape") {
          setAddMenuOpen(false);
          setShowConnectionsInAddMenu(false);
          setSkillsMenuOpen(false);
          setActiveConnection(null);
          setMentionMode(null);
        }

        return false;
      },
      [editor, handleSendMessage, onSend],
    );

    useEffect(() => {
      return editor.registerCommand(
        KEY_DOWN_COMMAND,
        handleKeyDown,
        COMMAND_PRIORITY_NORMAL,
      );
    }, [handleKeyDown]);

    // Handle / keypress trigger from SkillPlugin
    useEffect(() => {
      return editor.registerCommand(
        TRIGGER_SKILL_MENU_COMMAND,
        ({ position }) => {
          setMentionMode("/");
          setMentionStartPos(position);
          setSkillsMenuOpen(true);
          setSkillsFilter("");
          // Close other menus
          setAddMenuOpen(false);
          setShowConnectionsInAddMenu(false);
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      );
    }, [editor]);

    // Handle @ keypress trigger from ConnectionPlugin
    useEffect(() => {
      return editor.registerCommand(
        TRIGGER_CONNECTION_MENU_COMMAND,
        ({ position }) => {
          setMentionMode("@");
          setMentionStartPos(position);
          setAddMenuOpen(true);
          setShowConnectionsInAddMenu(true);
          setActiveConnection(null);
          setConnectionsFilter("");
          // Close other menus
          setSkillsMenuOpen(false);
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      );
    }, [editor]);

    useEffect(() => {
      onReferencedEntitiesChange?.(editorReferencedEntities);
    }, [editorReferencedEntities]);

    const focusTextarea = () => {
      editor.focus();
    };

    const handleTranscriptionComplete = useCallback(
      (transcribedText: string) => {
        if (!transcribedText.trim()) return;

        const currentMessage = message || "";
        const newMessage = currentMessage
          ? `${currentMessage} ${transcribedText}`
          : transcribedText;

        setMessage(newMessage);

        setTimeout(() => {
          focusTextarea();
        }, 0);
      },
      [message, setMessage],
    );

    const handleTranscriptionAndSend = useCallback(
      async (transcribedText: string) => {
        if (!transcribedText.trim()) return;

        const currentMessage = message || "";
        const combined = currentMessage
          ? `${currentMessage} ${transcribedText}`
          : transcribedText;

        await onSend?.({
          id: uuidv4(),
          content: combined.trim(),
          referenced_entities: sendPayloadReferencedEntities,
          reasoning_effort: agentReasoningEffort,
        });

        setMessage("");
        setEditorReferencedEntities([]);
        clearDraft();
        // agentReasoningEffort is intentionally NOT reset
      },
      [
        message,
        attachedFiles,
        sendPayloadReferencedEntities,
        agentReasoningEffort,
        onSend,
        setMessage,
        clearDraft,
      ],
    );

    const handleMicrophoneClick = useCallback(async () => {
      if (!microphoneGranted) {
        // Request permission if not granted
        const result = await requestMicrophonePermission();
        if (result.state !== "granted") {
          toast.error(result.message);
          return;
        }
        toast.success(
          "Microphone access granted! You can now use voice recording.",
        );
      }
    }, [microphoneGranted, requestMicrophonePermission]);

    // Usage disabled message component
    const UsageDisabledMessage = () => {
      const getMessage = () => {
        switch (usageLimitReason) {
          case "trial_credits_exceeded":
            // A card already on file — the fix is to end the trial, not add one.
            return hasPaymentMethod
              ? "Your trial credits have been used up. End your trial now to continue using Cohesive."
              : "Your trial credits have been used up. Add a payment method to continue using Cohesive.";
          case "overage_limit_exceeded":
            return "You've exceeded your usage limit for this billing period. Upgrade your plan or wait for the next billing cycle.";
          default:
            return "Usage temporarily restricted. Please check your billing settings.";
        }
      };

      return (
        <div className="flex items-center justify-center p-2 mb-2 bg-muted/50 border rounded-lg text-xs text-muted-foreground">
          <AlertTriangle className="w-4 h-4 mr-2 text-lc-warning" />
          <span>{getMessage()}</span>
        </div>
      );
    };

    // Shared EditorContent props
    const editorContentProps = {
      key: editorKey,
      skills,
      integrationConnections,
      placeholder: isUsageDisabled
        ? "Message input disabled due to usage limits"
        : placeholder,
      className: resolvedClassNames.editor,
      placeholderClassName: resolvedClassNames.placeholder,
      autoFocus: autoFocus && !isUsageDisabled,
      addPlaceholderPluginMode: addPlaceholderMode,
    };

    return (
      <div
        ref={containerRef}
        className={cn(resolvedClassNames.container, className)}
      >
        {/* Show usage disabled message when usage is restricted */}
        {isUsageDisabled && <UsageDisabledMessage />}

        {/* Bypass banner: shown above the composer while the session is
            auto-accepting every command, with a one-click return to
            confirmation mode. Renders nothing otherwise. */}
        <SessionBypassNotice flowSessionId={flowSessionId} className="mb-1.5" />

        <ComposerShell
          className={cn(resolvedClassNames.contentWrapper, {
            "opacity-50 pointer-events-none": isUsageDisabled,
          })}
          attachmentsRow={
            attachedFiles.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 px-1 pt-0.5">
                {attachedFiles.map((file) => (
                  <MessageInputFilePreview
                    key={file.id}
                    file={file}
                    onRemoveFile={onRemoveFile}
                  />
                ))}
              </div>
            ) : undefined
          }
          toolbar={
            <div
              className={cn(
                "flex items-center w-full",
                middleToolbarSlot != null ? "gap-2" : "justify-between",
                resolvedClassNames.toolbarRow,
              )}
            >
              <TooltipProvider>
                <div className={resolvedClassNames.leftControls}>
                  {/* 1. AddMenuButton (@ icon) - order-1 */}
                  {showAddMenu && !isRecording && (
                    <AddMenuButton
                      iconClassName={resolvedClassNames.menuIcon}
                      triggerButtonClassName={
                        resolvedClassNames.toolbarIconButton
                      }
                      onAttachFile={onAttachFile}
                      onAttachFileRecords={onAttachFileRecords}
                      libraryWorkspaceId={resolvedLibraryWorkspaceId}
                      flowSessionId={flowSessionId}
                      filteredConnections={filteredConnections}
                      hasConnections={hasConnections}
                      connectionsLoading={connectionsLoading}
                      connectionsError={connectionsError}
                      connectionsEnabled={connectionsEnabled}
                      fileUploadEnabled={fileUploadEnabled}
                      containerRect={containerRect}
                      addMenuOpen={addMenuOpen}
                      setAddMenuOpen={setAddMenuOpen}
                      showConnectionsInAddMenu={showConnectionsInAddMenu}
                      setShowConnectionsInAddMenu={setShowConnectionsInAddMenu}
                      activeConnection={activeConnection}
                      setActiveConnection={setActiveConnection}
                      setMentionMode={setMentionMode}
                      focusTextarea={focusTextarea}
                    />
                  )}

                  {/* 2. SkillsMenu (/ icon) - order-2 */}
                  {showSkills && !isRecording && (
                    <SkillsMenu
                      iconClassName={resolvedClassNames.menuIcon}
                      triggerButtonClassName={
                        resolvedClassNames.toolbarIconButton
                      }
                      skillsEnabled={skillsEnabled}
                      filteredSkills={filteredSkills}
                      skillsFilter={skillsFilter}
                      setSkillsFilter={setSkillsFilter}
                      skillsMenuOpen={skillsMenuOpen}
                      setSkillsMenuOpen={setSkillsMenuOpen}
                      mentionMode={mentionMode}
                      setMentionMode={setMentionMode}
                      focusTextarea={focusTextarea}
                      leftBoundary={containerRect?.left || 0}
                      containerRect={containerRect}
                    />
                  )}

                  {/* 3. ReasoningEffortButton (pill) - order-3, canvas only, right of @ and / */}
                  {showReasoningEffort && !isRecording && (
                    <div className="order-3 flex items-center">
                      <Separator
                        orientation="vertical"
                        className="mx-1 h-[18px]"
                      />
                      <ReasoningEffortButton
                        effort={agentReasoningEffort ?? "standard"}
                        onChange={(effort) => {
                          onAgentReasoningEffortChange?.(effort);
                        }}
                      />
                    </div>
                  )}

                  {/* 5. PlaceholderButton ({} icon) - order-5 */}
                  {showPlaceholders &&
                    addPlaceholderMode === "edit" &&
                    !isRecording && (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              editor.dispatchCommand(
                                INSERT_PLACEHOLDER_COMMAND,
                                {
                                  placeholder: "",
                                  value: "",
                                  mode: "edit",
                                  focus: true,
                                },
                              );
                            }}
                          >
                            <Braces
                              className={resolvedClassNames.placeholderIcon}
                            />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          Add placeholder
                        </TooltipContent>
                      </Tooltip>
                    )}
                </div>
              </TooltipProvider>

              {middleToolbarSlot != null && (
                <div className="flex-1 min-w-0 flex items-center justify-start">
                  {middleToolbarSlot}
                </div>
              )}

              <div className={resolvedClassNames.rightControls}>
                {/* 2. Audio Recording button */}
                {showMicrophone && (
                  <>
                    {microphoneEnabled ? (
                      <AudioRecordingButton
                        onTranscriptionComplete={handleTranscriptionComplete}
                        onSendImmediately={
                          onSend ? handleTranscriptionAndSend : undefined
                        }
                        className={resolvedClassNames.audioRecordingButton}
                        sendButtonClassName={resolvedClassNames.sendButton}
                        sendIconClassName={resolvedClassNames.sendIcon}
                        micIconClassName={resolvedClassNames.microphoneIcon}
                        disabled={sendButtonDisabled}
                        ref={audioRecordingRef}
                        onRecordingStateChange={setIsRecording}
                      />
                    ) : (
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className={cn(
                                "transition-all duration-200 text-muted-foreground hover:text-foreground",
                                resolvedClassNames.microphoneButton,
                              )}
                              onClick={handleMicrophoneClick}
                            >
                              <Mic
                                className={resolvedClassNames.microphoneIcon}
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">Dictate</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </>
                )}

                {/* 3. Send/Stop button — hidden while AudioRecordingButton shows its own send */}
                {showSendButton && !isRecording && (
                  <SendButton
                    iconClassName={resolvedClassNames.sendIcon}
                    sendButtonClassName={resolvedClassNames.sendButton}
                    stopButtonClassName={resolvedClassNames.stopButton}
                    flowSession={flowSession}
                    handleStopFlowSession={handleStopFlowSession}
                    cancelFlowSessionPending={cancelFlowSession.isPending}
                    message={message}
                    onSend={onSend}
                    handleSendMessage={handleSendMessage}
                    isSendButtonDisabled={isSendButtonDisabled}
                  />
                )}
              </div>
            </div>
          }
        >
          <EditorContent {...editorContentProps} />
        </ComposerShell>
      </div>
    );
  },
);

export default React.memo(MessageInput);
